import { useEffect, useState } from 'react';
import {
  Anchor,
  Button,
  Card,
  Container,
  Group,
  List,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../shared/auth/auth.store';
import { useDemoMode } from '../../shared/demo/useDemoMode';
import { homeRouteForRole } from '../../shared/auth/home-route';
import { expeditorProfileApi } from '../../shared/api/expeditor-profile';

export function LandingPage() {
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);
  const isAuthed = Boolean(accessToken && user);
  const demoMode = useDemoMode();

  const [expeditorName, setExpeditorName] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    expeditorProfileApi
      .getPublicName()
      .then((d) => {
        if (!cancelled) setExpeditorName(d.name);
      })
      .catch(() => {
        // Ошибки на лендинге глотаем — пользователь увидит общее название.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Container size="md" py="xl">
      <Stack gap="xl">
        <Stack gap="xs" align="center">
          {expeditorName && (
            <Text c="dimmed" size="sm" ta="center" tt="uppercase" fw={500}>
              {expeditorName}
            </Text>
          )}
          <Title order={1} ta="center">
            Информационная система экспедитора
          </Title>
          <Text c="dimmed" ta="center" size="lg" maw={620}>
            Управление транспортно-экспедиционной деятельностью с встроенным
            соблюдением Постановления Правительства РФ&nbsp;№&nbsp;1317
            от&nbsp;19.09.2025.
          </Text>
        </Stack>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <Card withBorder padding="md" radius="md">
            <Title order={4}>Поручения и контракты</Title>
            <Text size="sm" c="dimmed" mt={6}>
              Приём поручений от клиентов, котировки экспедитора, электронный
              акцепт с моментом заключения договора и фиксацией сторон.
            </Text>
          </Card>
          <Card withBorder padding="md" radius="md">
            <Title order={4}>Перевозки и груз</Title>
            <Text size="sm" c="dimmed" mt={6}>
              Привязка перевозчика, события груза (погрузка, в пути, доставка),
              геотрек и статус доставки в реальном времени.
            </Text>
          </Card>
          <Card withBorder padding="md" radius="md">
            <Title order={4}>Платежи</Title>
            <Text size="sm" c="dimmed" mt={6}>
              Учёт оплат по контрактам, баланс задолженности, ручной ввод и
              автоматическая загрузка из 1С.
            </Text>
          </Card>
          <Card withBorder padding="md" radius="md">
            <Title order={4}>Compliance ПП&nbsp;1317</Title>
            <Text size="sm" c="dimmed" mt={6}>
              Append-only журналы регистраций, авторизаций, изменений профиля и
              операций. Готовая выгрузка для уполномоченного органа.
            </Text>
          </Card>
        </SimpleGrid>

        <Paper withBorder p="lg" radius="md">
          <Title order={4} mb="xs">
            Соответствие требованиям ПП&nbsp;РФ&nbsp;№&nbsp;1317
          </Title>
          <Text size="sm" c="dimmed" mb="sm">
            Система автоматически фиксирует все юридически значимые действия
            пользователей с указанием точного времени, часового пояса, IP-адреса
            и идентификатора сессии. Записи в журналах не подлежат удалению или
            изменению.
          </Text>
          <List size="sm" spacing={4}>
            <List.Item>регистрация и снятие с учёта пользователей (п. 3&nbsp;а, г)</List.Item>
            <List.Item>авторизации и попытки входа (п. 3&nbsp;б, в, д)</List.Item>
            <List.Item>изменения профиля и реквизитов (п. 3&nbsp;е)</List.Item>
            <List.Item>оформление поручений, заключение контрактов (п. 3&nbsp;ж, з)</List.Item>
            <List.Item>движение груза, геотрек, события перевозки (п. 3&nbsp;к)</List.Item>
            <List.Item>оплаты и движение средств (п. 3&nbsp;и)</List.Item>
          </List>
        </Paper>

        {isAuthed && user ? (
          <Stack gap="xs" align="center">
            <Text size="sm" c="dimmed">
              Вы вошли как <strong>{user.email}</strong>
            </Text>
            <Button component={Link} to={homeRouteForRole(user.role)} size="md">
              Перейти в кабинет
            </Button>
          </Stack>
        ) : demoMode ? (
          <Stack gap="xs" align="center">
            <Button component={Link} to="/demo" size="md" color="orange">
              Открыть демо-стенд
            </Button>
            <Text size="sm" c="dimmed">
              <Anchor component={Link} to="/login">
                Уже зарегистрированы — войти
              </Anchor>
            </Text>
          </Stack>
        ) : (
          <Group justify="center" gap="md">
            <Button component={Link} to="/login" size="md">
              Войти
            </Button>
            <Button component={Link} to="/register" size="md" variant="default">
              Зарегистрироваться
            </Button>
          </Group>
        )}
      </Stack>
    </Container>
  );
}
